import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import { useAuth } from "../lib/AuthContext";
import { api, formatErr } from "../lib/api";

export function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, pwd);
      const redirect = new URLSearchParams(loc.search).get("redirect") || "/app";
      nav(redirect);
    } catch (e) { setErr(e.message || "Erreur"); }
    setLoading(false);
  };

  return (
    <>
      <PublicNav />
      <div className="auth-wrap">
        <div className="auth-visual">
          <div className="auth-visual-content">
            <h1>Ravi de vous <em>revoir</em></h1>
            <p>Connectez-vous pour piloter votre activité depuis n'importe quel appareil.</p>
          </div>
          <ul className="auth-visual-list">
            <li>Devis, factures, BC en un clic</li>
            <li>Relances automatiques</li>
            <li>Conformité Polynésie française</li>
          </ul>
        </div>
        <div className="auth-form-wrap">
          <form className="auth-form" onSubmit={submit} data-testid="login-form">
            <h2>Connexion</h2>
            <p className="subtitle">Accédez à votre espace Gestion Lagon.</p>
            {err && <div className="err" data-testid="login-error">{err}</div>}
            <div className="field">
              <label>Email</label>
              <input type="email" className="input" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div className="field">
              <label>Mot de passe</label>
              <div style={{position:'relative'}}>
                <input type={showPwd ? "text" : "password"} className="input" value={pwd} onChange={e=>setPwd(e.target.value)} required data-testid="login-password" style={{paddingRight:42}} />
                <button type="button" onClick={()=>setShowPwd(!showPwd)} aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"} data-testid="login-password-toggle" style={{position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', color:'var(--ink-muted)', padding:6, display:'flex', alignItems:'center'}}>
                  {showPwd ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading} data-testid="login-submit">
              {loading ? <span className="spinner"></span> : "Se connecter"}
            </button>
            <div className="foot">
              <Link to="/forgot-password" data-testid="forgot-link">Mot de passe oublié ?</Link>
              <div style={{marginTop:12}}>Pas encore de compte ? <Link to="/signup" data-testid="signup-link"><strong>Essai gratuit</strong></Link></div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export function Signup() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [f, setF] = useState({ company_name: "", name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email);
  const passwordValid = f.password.length >= 8;
  const formValid = f.company_name.trim() && f.name.trim() && emailValid && passwordValid;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(f);
      nav("/app");
    } catch (e) { setErr(e.message || "Erreur"); }
    setLoading(false);
  };

  return (
    <>
      <PublicNav />
      <div className="auth-wrap">
        <div className="auth-visual">
          <div className="auth-visual-content">
            <h1>Bienvenue dans le <em>lagon</em></h1>
            <p>Créez votre compte et démarrez immédiatement votre essai Pro de 14 jours — sans CB demandée à l'inscription.</p>
          </div>
          <ul className="auth-visual-list">
            <li>Gratuit et sans engagement pendant l'essai</li>
            <li>Toutes les fonctionnalités Pro débloquées</li>
            <li>Installation en 2 minutes · sans formation</li>
            <li>Support français disponible</li>
          </ul>
        </div>
        <div className="auth-form-wrap">
          <form className="auth-form" onSubmit={submit} data-testid="signup-form">
            <h2>Créer mon compte</h2>
            <p className="subtitle">14 jours d'essai Pro gratuit · sans CB à l'inscription.</p>
            {err && <div className="err" data-testid="signup-error">{err}</div>}
            <div className="field">
              <label>Nom de votre entreprise</label>
              <input type="text" className="input" value={f.company_name} onChange={e=>setF({...f, company_name:e.target.value})} required data-testid="signup-company" />
            </div>
            <div className="field">
              <label>Votre nom complet</label>
              <input type="text" className="input" value={f.name} onChange={e=>setF({...f, name:e.target.value})} required data-testid="signup-name" />
            </div>
            <div className="field">
              <label>Email professionnel</label>
              <input type="email" className="input" value={f.email} onChange={e=>setF({...f, email:e.target.value})} required data-testid="signup-email" />
              {f.email && !emailValid && <div style={{fontSize:12, color:'var(--danger)', marginTop:4}} data-testid="signup-email-err">Format d'email invalide.</div>}
            </div>
            <div className="field">
              <label>Mot de passe (8 caractères minimum)</label>
              <div style={{position:'relative'}}>
                <input type={showPassword ? "text" : "password"} className="input" value={f.password} onChange={e=>setF({...f, password:e.target.value})} minLength="8" required data-testid="signup-password" style={{paddingRight:42}} />
                <button type="button" onClick={()=>setShowPassword(!showPassword)} aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} data-testid="signup-password-toggle" style={{position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', color:'var(--ink-muted)', padding:6, display:'flex', alignItems:'center'}}>
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {f.password && !passwordValid && <div style={{fontSize:12, color:'var(--warn)', marginTop:4}} data-testid="signup-password-hint">Encore {8-f.password.length} caractère{8-f.password.length>1?"s":""}.</div>}
              {passwordValid && <div style={{fontSize:12, color:'var(--ok)', marginTop:4}} data-testid="signup-password-ok">✓ Mot de passe valide</div>}
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || !formValid} data-testid="signup-submit">
              {loading ? <span className="spinner"></span> : "Commencer mon essai gratuit"}
            </button>
            <div className="foot">
              Déjà un compte ? <Link to="/login"><strong>Se connecter</strong></Link>
            </div>
            <p style={{fontSize:11, color:'var(--ink-faint)', textAlign:'center', marginTop:16}}>En créant votre compte, vous acceptez nos conditions d'utilisation.</p>
          </form>
        </div>
      </div>
    </>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api("/auth/forgot-password", { method: "POST", body: { email } });
      setSent(true);
    } catch (e) { setErr(e.message || "Erreur"); }
    setLoading(false);
  };

  return (
    <>
      <PublicNav />
      <div className="auth-wrap">
        <div className="auth-visual">
          <div className="auth-visual-content">
            <h1>Mot de passe <em>oublié</em> ?</h1>
            <p>Pas de panique, on vous envoie un lien pour en créer un nouveau.</p>
          </div>
        </div>
        <div className="auth-form-wrap">
          <form className="auth-form" onSubmit={submit} data-testid="forgot-form">
            <h2>Réinitialiser</h2>
            <p className="subtitle">Entrez votre email, nous vous enverrons un lien de réinitialisation.</p>
            {err && <div className="err">{err}</div>}
            {sent && <div className="ok" data-testid="forgot-ok">Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.</div>}
            <div className="field">
              <label>Email</label>
              <input type="email" className="input" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="forgot-email" />
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || sent} data-testid="forgot-submit">
              {loading ? <span className="spinner"></span> : "Envoyer le lien"}
            </button>
            <div className="foot">
              <Link to="/login">← Retour à la connexion</Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export function ResetPassword() {
  const loc = useLocation();
  const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd !== pwd2) { setErr("Les mots de passe ne correspondent pas."); return; }
    if (pwd.length < 8) { setErr("Au moins 8 caractères."); return; }
    setLoading(true);
    try {
      await api("/auth/reset-password", { method: "POST", body: { token, new_password: pwd } });
      setOk(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (e) { setErr(e.message || "Erreur"); }
    setLoading(false);
  };

  return (
    <>
      <PublicNav />
      <div className="auth-wrap">
        <div className="auth-visual">
          <div className="auth-visual-content">
            <h1>Nouveau <em>mot de passe</em></h1>
            <p>Choisissez un mot de passe solide pour sécuriser votre compte.</p>
          </div>
        </div>
        <div className="auth-form-wrap">
          <form className="auth-form" onSubmit={submit} data-testid="reset-form">
            <h2>Définir un nouveau mot de passe</h2>
            {err && <div className="err">{err}</div>}
            {ok && <div className="ok">Mot de passe modifié. Redirection…</div>}
            <div className="field">
              <label>Nouveau mot de passe (8 caractères minimum)</label>
              <input type="password" className="input" value={pwd} onChange={e=>setPwd(e.target.value)} minLength="8" required data-testid="reset-pwd" />
            </div>
            <div className="field">
              <label>Confirmer</label>
              <input type="password" className="input" value={pwd2} onChange={e=>setPwd2(e.target.value)} minLength="8" required data-testid="reset-pwd2" />
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || ok || !token} data-testid="reset-submit">
              {loading ? <span className="spinner"></span> : "Réinitialiser"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
